/**
 * Samsung TV game mode requires the minimum and maximum video resolution and
 * the maximum frame rate to be declared in the SDP before streaming starts, so
 * the platform can set up its low-latency decode path up front.
 *
 * On FHD panels the declared maximum must be 1920 x 1080 — declaring anything
 * larger leaves the game video unavailable, so the bounds are derived from the
 * panel the app is actually running on.
 *
 * https://developer.samsung.com/smarttv/develop/guides/cloud_gaming/cg_implement_webapp.html
 */
import { getResolution } from "./deviceInfo";

export interface VideoImageAttrBounds {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  maxFps: number;
}

const MIN_WIDTH = 960;
const MIN_HEIGHT = 540;
const MAX_FPS = 60;

/** Dynamic 960 x 540 up to 1920 x 1080, 60 fps. */
export const FHD_IMAGE_ATTR_BOUNDS: VideoImageAttrBounds = {
  minWidth: MIN_WIDTH,
  minHeight: MIN_HEIGHT,
  maxWidth: 1920,
  maxHeight: 1080,
  maxFps: MAX_FPS,
};

/** Dynamic 960 x 540 up to 2560 x 1440, 60 fps — the ceiling the app streams. */
export const QHD_IMAGE_ATTR_BOUNDS: VideoImageAttrBounds = {
  minWidth: MIN_WIDTH,
  minHeight: MIN_HEIGHT,
  maxWidth: 2560,
  maxHeight: 1440,
  maxFps: MAX_FPS,
};

/** Panel height above which more than FHD may be declared. */
const UHD_PANEL_HEIGHT = 2160;

/**
 * Bounds for the panel of the current device. Falls back to FHD, which every
 * panel can display, whenever the panel height cannot be established.
 */
export function getImageAttrBounds(): VideoImageAttrBounds {
  return getResolution() >= UHD_PANEL_HEIGHT
    ? QHD_IMAGE_ATTR_BOUNDS
    : FHD_IMAGE_ATTR_BOUNDS;
}

/** Payload types that carry retransmissions or FEC rather than the video itself. */
const NON_VIDEO_ENCODINGS = /^(rtx|red|ulpfec|flexfec-03)$/i;

const RTPMAP = /^a=rtpmap:(\d+)\s+([^/]+)\//;

function buildImageAttrLine(
  payloadType: string,
  bounds: VideoImageAttrBounds,
): string {
  const { minWidth, maxWidth, minHeight, maxHeight, maxFps } = bounds;
  return (
    `a=imageattr:${payloadType} send ` +
    `[x=[${minWidth}:${maxWidth}],y=[${minHeight}:${maxHeight}],fps=[${maxFps}:${maxFps}]]`
  );
}

/**
 * Declares `bounds` for every video codec in the SDP's video media section.
 *
 * The attribute is written in the `send` direction because it describes the
 * stream the game server sends to the TV; existing `imageattr` lines in that
 * section are replaced so the panel's ceiling always wins. The SDP is returned
 * untouched when it carries no video codec.
 */
export function applyVideoImageAttr(
  sdp: string,
  bounds: VideoImageAttrBounds,
): string {
  const eol = sdp.includes("\r\n") ? "\r\n" : "\n";
  const lines = sdp.split(/\r?\n/);

  const sectionStart = lines.findIndex((line) => line.startsWith("m=video"));
  if (sectionStart === -1) return sdp;

  const nextSection = lines.findIndex(
    (line, index) => index > sectionStart && line.startsWith("m="),
  );
  const sectionEnd = nextSection === -1 ? lines.length : nextSection;

  const payloadTypes: string[] = [];
  for (let index = sectionStart; index < sectionEnd; index++) {
    const rtpmap = RTPMAP.exec(lines[index]);
    if (rtpmap && !NON_VIDEO_ENCODINGS.test(rtpmap[2])) {
      payloadTypes.push(rtpmap[1]);
    }
  }
  if (payloadTypes.length === 0) return sdp;

  const section = lines
    .slice(sectionStart, sectionEnd)
    .filter((line) => !line.startsWith("a=imageattr:"));

  // A well-formed SDP is terminated by a line ending, which leaves a trailing
  // empty entry the attribute has to stay in front of.
  let insertAt = section.length;
  while (insertAt > 0 && section[insertAt - 1] === "") insertAt--;
  section.splice(
    insertAt,
    0,
    ...payloadTypes.map((type) => buildImageAttrLine(type, bounds)),
  );

  return [
    ...lines.slice(0, sectionStart),
    ...section,
    ...lines.slice(sectionEnd),
  ].join(eol);
}
